/**
 * Framework-agnostic chat state machine. Owns everything except rendering:
 *   - session id + history persistence (localStorage)
 *   - messages array + live token accumulation
 *   - streaming lifecycle + abort
 *   - attachment upload + scan-status polling
 *
 * The element (or any other renderer) subscribes via `onChange` and calls
 * methods like `sendMessage`, `addFiles`, `removeAttachment`. No DOM
 * references live here.
 */
import { AuthManager } from './auth.js';
import {
  fetchConfig,
  openMessageStream,
  requestUploadUrl,
  confirmUpload,
  pollFileStatus,
} from './api.js';
import { parseSseStream } from './sse.js';
import type { AuthProvider, ChatConfig } from './types.js';

export type Role = 'user' | 'assistant';

export interface Msg {
  role: Role;
  content: string;
  toolCalls?: Array<{ name: string; input: unknown; output?: string; running?: boolean }>;
  attachments?: Array<{ id: string; originalName: string; mimeType: string; size: number }>;
}

export type AttachmentStatus = 'uploading' | 'scanning' | 'clean' | 'infected' | 'scan_failed' | 'error';

export interface Attachment {
  clientId: string;
  file: File;
  fileId?: string;
  status: AttachmentStatus;
  progress: number;
  error?: string;
}

export interface ChatState {
  config: ChatConfig | null;
  sessionId: string | null;
  messages: Msg[];
  input: string;
  streaming: boolean;
  activeTool: string | null;
  error: string | null;
  attachments: Attachment[];
  dragActive: boolean;
  bootError: string | null;
}

export interface ChatControllerOptions {
  chatId: string;
  apiBase: string;
  authProvider: AuthProvider | null;
  authEndpoint: string | null;
  /** Override attribute — if set, overrides the server's theme.mode config. */
  themeOverride?: 'dark' | 'light' | 'auto';
  /** Override attribute — if set, overrides the server's primaryColor config. */
  primaryColorOverride?: string;
  /** Dashboard preview iframe only — short-lived token sent as X-Preview-Token. */
  previewToken?: string | null;
}

export class ChatController {
  readonly options: ChatControllerOptions;
  state: ChatState;
  private previewToken: string | null;

  private auth: AuthManager;
  private listeners = new Set<(s: ChatState) => void>();
  private abortController: AbortController | null = null;
  private pollInterval: ReturnType<typeof setInterval> | null = null;

  constructor(opts: ChatControllerOptions) {
    this.options = opts;
    this.previewToken = opts.previewToken ?? null;
    this.auth = new AuthManager(opts.authProvider, opts.authEndpoint);
    this.state = {
      config: null,
      sessionId: null,
      messages: [],
      input: '',
      streaming: false,
      activeTool: null,
      error: null,
      attachments: [],
      dragActive: false,
      bootError: null,
    };
  }

  subscribe(fn: (s: ChatState) => void): () => void {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  }

  /** Swap the preview token at runtime — the dashboard refreshes it every
   *  25 minutes over postMessage so long preview sessions stay authenticated
   *  without forcing an iframe reload. */
  setPreviewToken(token: string | null): void {
    this.previewToken = token;
  }

  /** Re-bind the auth provider after construction. The React wrapper needs
   *  this because `connectedCallback` fires before React's `useEffect` runs,
   *  so the controller boots with whatever was on the element at append time
   *  (null when only `authProvider` was passed as a JSX prop). Without this
   *  hook the signed Authorization header would never be sent. */
  setAuthProvider(provider: AuthProvider | null): void {
    this.auth.setProvider(provider);
  }

  /** Read-only access to the AuthManager so the voice flow can sign its
   *  call to /api/voice-sessions/start with the same Bearer-HMAC scheme
   *  the chat message endpoint uses. Kept as a getter so the field stays
   *  private — callers can't swap the manager out. */
  get authManager(): AuthManager {
    return this.auth;
  }

  /** Same idea for declarative auth — kept symmetric. */
  setAuthEndpoint(endpoint: string | null): void {
    this.auth.setEndpoint(endpoint);
  }

  async boot(): Promise<void> {
    try {
      const config = await fetchConfig(this.options.apiBase, this.options.chatId);
      const sessionId = this.loadOrCreateSession();
      const savedMessages = this.loadHistory();
      this.setState({
        config,
        sessionId,
        messages: savedMessages.length > 0
          ? savedMessages
          : config.initialMessages.map<Msg>((m) => ({ role: m.role, content: m.content })),
      });
    } catch (err) {
      this.setState({ bootError: err instanceof Error ? err.message : 'Failed to load chat' });
    }
  }

  setInput(text: string): void { this.setState({ input: text }); }

  async sendMessage(text: string): Promise<void> {
    const { sessionId, streaming, attachments } = this.state;
    if (!sessionId || streaming) return;
    const trimmed = text.trim();
    const ready = attachments.filter((a) => a.status === 'clean' && a.fileId);
    if (attachments.length !== ready.length) {
      this.setState({ error: 'Wait for all attachments to finish scanning, or remove the flagged ones.' });
      return;
    }
    if (!trimmed && ready.length === 0) return;

    const attachmentSnapshot = ready.map((a) => ({
      id: a.fileId!,
      originalName: a.file.name,
      mimeType: a.file.type,
      size: a.file.size,
    }));

    this.setState({
      input: '',
      error: null,
      streaming: true,
      activeTool: null,
      attachments: [],
      messages: [
        ...this.state.messages,
        { role: 'user', content: trimmed, attachments: attachmentSnapshot.length > 0 ? attachmentSnapshot : undefined },
        { role: 'assistant', content: '' },
      ],
    });

    const ctrl = new AbortController();
    this.abortController = ctrl;

    try {
      const res = await openMessageStream(
        this.options.apiBase,
        this.options.chatId,
        this.auth,
        {
          message: trimmed,
          sessionId,
          ...(ready.length > 0 ? { fileRefs: ready.map((a) => ({ fileId: a.fileId! })) } : {}),
        },
        ctrl.signal,
        this.previewToken,
      );
      if (!res.ok) {
        const errBody = await res.json().catch(() => null);
        throw new Error((errBody as { error?: string } | null)?.error ?? `HTTP ${res.status}`);
      }
      if (!res.body) throw new Error('No response body');

      for await (const frame of parseSseStream(res.body)) {
        this.applyFrame(frame.event, frame.data);
      }
      this.persistHistory();
    } catch (err) {
      if ((err as { name?: string } | null)?.name === 'AbortError') return;
      const message = err instanceof Error ? err.message : 'Failed to reach the chat server';
      this.setState({ error: message });
      this.replaceLastAssistantIfEmpty(`**Error:** ${message}`);
    } finally {
      this.abortController = null;
      this.setState({ streaming: false, activeTool: null });
    }
  }

  stop(): void {
    this.abortController?.abort();
  }

  reset(): void {
    if (!this.state.config) return;
    const sid = this.createSessionId();
    this.writeSession(sid);
    this.clearHistory();
    this.setState({
      sessionId: sid,
      messages: this.state.config.initialMessages.map<Msg>((m) => ({ role: m.role, content: m.content })),
      error: null,
      attachments: [],
      input: '',
    });
  }

  setDragActive(active: boolean): void {
    if (this.state.dragActive !== active) this.setState({ dragActive: active });
  }

  /**
   * Append a message to the live conversation. Used by the voice
   * session to surface transcript turns inline as the call streams,
   * so the visitor sees the same text history they'd see in a
   * text-only chat.
   */
  appendMessage(role: 'user' | 'assistant', content: string): void {
    const trimmed = content.trim();
    if (!trimmed) return;
    this.setState({ messages: [...this.state.messages, { role, content: trimmed }] });
  }

  addFiles(files: FileList | File[]): void {
    const cfg = this.state.config;
    const sessionId = this.state.sessionId;
    if (!cfg?.attachments?.enabled || !sessionId) return;

    const allowed = new Set(cfg.attachments.allowedMimeTypes);
    const maxFileSize = cfg.attachments.maxFileSize;
    const maxPerMessage = cfg.attachments.maxPerMessage;

    const toAdd: Attachment[] = [];
    for (const file of Array.from(files)) {
      if (this.state.attachments.length + toAdd.length >= maxPerMessage) {
        this.setState({ error: `You can attach up to ${maxPerMessage} files per message.` });
        break;
      }
      const mime = (file.type || 'application/octet-stream').toLowerCase();
      if (allowed.size > 0 && !allowed.has(mime)) {
        this.setState({ error: `File type "${mime}" is not allowed here.` });
        continue;
      }
      if (maxFileSize > 0 && file.size > maxFileSize) {
        this.setState({ error: `"${file.name}" is too large — max ${formatBytes(maxFileSize)}.` });
        continue;
      }
      toAdd.push({ clientId: randomId(), file, status: 'uploading', progress: 0 });
    }
    if (toAdd.length === 0) return;
    this.setState({ error: null, attachments: [...this.state.attachments, ...toAdd] });
    for (const a of toAdd) void this.startUpload(a);
    this.ensurePoller();
  }

  removeAttachment(clientId: string): void {
    this.setState({ attachments: this.state.attachments.filter((a) => a.clientId !== clientId) });
  }

  private async startUpload(att: Attachment): Promise<void> {
    const { apiBase, chatId } = this.options;
    const sessionId = this.state.sessionId!;
    try {
      const { fileId, uploadUrl } = await requestUploadUrl(apiBase, chatId, {
        fileName: att.file.name,
        mimeType: att.file.type || 'application/octet-stream',
        sessionId,
        size: att.file.size,
      });
      this.updateAttachment(att.clientId, { fileId });

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', att.file.type || 'application/octet-stream');
        xhr.upload.onprogress = (ev) => {
          if (!ev.lengthComputable) return;
          this.updateAttachment(att.clientId, { progress: Math.round((ev.loaded / ev.total) * 100) });
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) return resolve();
          const body = (xhr.responseText || '').slice(0, 200);
          reject(new Error(`R2 rejected upload (HTTP ${xhr.status})${body ? `: ${body}` : ''}`));
        };
        xhr.onerror = () => reject(new Error('Upload blocked — check R2 bucket CORS allows PUT from this origin'));
        xhr.send(att.file);
      });

      await confirmUpload(apiBase, chatId, fileId);
      this.updateAttachment(att.clientId, { status: 'scanning', progress: 100 });
      this.ensurePoller();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'upload failed';
      this.updateAttachment(att.clientId, { status: 'error', error: msg });
    }
  }

  private ensurePoller(): void {
    if (this.pollInterval) return;
    this.pollInterval = setInterval(async () => {
      const scanning = this.state.attachments.filter((a) => a.status === 'scanning' && a.fileId);
      if (scanning.length === 0) {
        if (this.pollInterval) { clearInterval(this.pollInterval); this.pollInterval = null; }
        return;
      }
      for (const a of scanning) {
        try {
          const { scanStatus, scanMessage } = await pollFileStatus(this.options.apiBase, this.options.chatId, a.fileId!);
          if (scanStatus === 'clean') this.updateAttachment(a.clientId, { status: 'clean' });
          else if (scanStatus === 'infected') this.updateAttachment(a.clientId, { status: 'infected', error: scanMessage });
          else if (scanStatus === 'scan_failed') this.updateAttachment(a.clientId, { status: 'scan_failed', error: scanMessage });
        } catch { /* transient — retry on next tick */ }
      }
    }, 600);
  }

  private updateAttachment(clientId: string, patch: Partial<Attachment>): void {
    this.setState({
      attachments: this.state.attachments.map((a) => (a.clientId === clientId ? { ...a, ...patch } : a)),
    });
  }

  private applyFrame(event: string, data: unknown): void {
    const d = data as Record<string, unknown>;
    switch (event) {
      case 'session': {
        if (typeof d.sessionId === 'string' && d.sessionId !== this.state.sessionId) {
          this.setState({ sessionId: d.sessionId });
          this.writeSession(d.sessionId);
        }
        return;
      }
      case 'token': {
        const text = typeof d.text === 'string' ? d.text : '';
        this.updateLastAssistant((last) => ({ ...last, content: last.content + text }));
        return;
      }
      case 'tool_start': {
        this.setState({ activeTool: typeof d.name === 'string' ? d.name : null });
        this.updateLastAssistant((last) => {
          const calls = last.toolCalls ? [...last.toolCalls] : [];
          calls.push({ name: String(d.name ?? ''), input: d.input, running: true });
          return { ...last, toolCalls: calls };
        });
        return;
      }
      case 'tool_end': {
        this.setState({ activeTool: null });
        this.updateLastAssistant((last) => {
          if (!last.toolCalls) return last;
          const calls = [...last.toolCalls];
          const idx = calls.findIndex((c) => c.name === d.name && c.running);
          if (idx >= 0) calls[idx] = { ...calls[idx], output: String(d.output ?? ''), running: false };
          return { ...last, toolCalls: calls };
        });
        return;
      }
      case 'done': {
        const response = typeof d.response === 'string' ? d.response : '';
        if (response) {
          this.updateLastAssistant((last) => ({ ...last, content: response }));
        }
        this.setState({ activeTool: null });
        return;
      }
      case 'error': {
        throw new Error(typeof d.message === 'string' ? d.message : 'Chat failed');
      }
    }
  }

  private updateLastAssistant(patch: (m: Msg) => Msg): void {
    const next = [...this.state.messages];
    const last = next[next.length - 1];
    if (last?.role !== 'assistant') return;
    next[next.length - 1] = patch(last);
    this.setState({ messages: next });
  }

  private replaceLastAssistantIfEmpty(content: string): void {
    this.updateLastAssistant((last) => (last.content ? last : { ...last, content }));
  }

  private setState(patch: Partial<ChatState>): void {
    this.state = { ...this.state, ...patch };
    for (const fn of this.listeners) fn(this.state);
  }

  /* ── localStorage helpers ────────────────────────────────────────────── */

  private storageKey(): string { return `hwchat:${this.options.chatId}:session`; }
  private historyKey(): string { return `hwchat:${this.options.chatId}:history`; }

  private loadOrCreateSession(): string {
    const existing = this.readSession();
    if (existing) return existing;
    const sid = this.createSessionId();
    this.writeSession(sid);
    return sid;
  }

  private readSession(): string | null {
    try { return localStorage.getItem(this.storageKey()); } catch { return null; }
  }

  private writeSession(sid: string): void {
    try { localStorage.setItem(this.storageKey(), sid); } catch { /* quota / disabled */ }
  }

  private createSessionId(): string {
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      const buf = new Uint8Array(8);
      crypto.getRandomValues(buf);
      return 'sess_' + Array.from(buf).map((b) => b.toString(16).padStart(2, '0')).join('');
    }
    return 'sess_' + Math.random().toString(16).slice(2, 18);
  }

  private loadHistory(): Msg[] {
    try {
      const raw = localStorage.getItem(this.historyKey());
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as Msg[]) : [];
    } catch { return []; }
  }

  private persistHistory(): void {
    try {
      const hasRealTurn = this.state.messages.some((m) => m.role === 'user');
      if (!hasRealTurn) return;
      localStorage.setItem(this.historyKey(), JSON.stringify(this.state.messages));
    } catch { /* quota */ }
  }

  private clearHistory(): void {
    try { localStorage.removeItem(this.historyKey()); } catch { /* ignore */ }
  }
}

function randomId(): string {
  return Math.random().toString(36).slice(2, 10);
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export { formatBytes };
